import { useCallback, useMemo } from 'react'

import {
  useLazyListMeetingsSupergraphQuery,
  useLazyMeetingsPageListSupergraphQuery,
  useLazyGetMeetingSupergraphQuery,
  useListMeetingsSupergraphQuery,
  useMeetingsPageListSupergraphQuery,
} from 'shared/api/meetings'

import { buildSupergraphInput, shouldUseMeetingsPageList } from './supergraph_meeting_search_utils'
import { VISIBLE_RANGE_PAGE_SIZE } from './visible_range_constants'
import {
  buildMeetingsSearchSupergraphCalendarSliceInput,
} from '../../meeting_search_supergraph_utils'

export const useSupergraphSearchDataQueries = ({
  skipQueries,
  isSupergraphEnabled,
  shouldLoadToday,
  forwardOnly = false,
  skipPastQuery = false,
  listOrder = 'ASC',
  filters,
  visibleRangeQueryVariables,
  queryOptions,
  backgroundPollingConfig,
  visibleRangePollingInterval,
}) => {
  const usePageList = shouldUseMeetingsPageList(filters)
  const useVisibleRangePageList = shouldUseMeetingsPageList(visibleRangeQueryVariables)

  const futureCmdInput = useMemo(
    () => (filters && !usePageList
      ? buildSupergraphInput(filters, {
        first: 30,
        after: null,
        order: listOrder,
        fromStartOfToday: forwardOnly,
      })
      : null),
    [filters, usePageList, forwardOnly, listOrder],
  )

  const futureSearchInput = useMemo(
    () => (filters && usePageList
      ? buildMeetingsSearchSupergraphCalendarSliceInput(filters, {
        first: 30,
        after: null,
        order: listOrder,
      })
      : null),
    [filters, usePageList, listOrder],
  )

  const todayCmdInput = useMemo(
    () => (filters && !usePageList
      ? buildSupergraphInput(filters, {
        today: true,
        first: 10_000,
        after: null,
        order: 'ASC',
      })
      : null),
    [filters, usePageList],
  )

  const todaySearchInput = useMemo(
    () => (filters && usePageList
      ? buildMeetingsSearchSupergraphCalendarSliceInput(filters, {
        today: true,
        first: 10_000,
        after: null,
        order: 'ASC',
      })
      : null),
    [filters, usePageList],
  )

  const pastCmdInput = useMemo(
    () => (filters && !usePageList
      ? buildSupergraphInput(filters, {
        past: true,
        first: 30,
        after: null,
        order: 'DESC',
      })
      : null),
    [filters, usePageList],
  )

  const pastSearchInput = useMemo(
    () => (filters && usePageList
      ? buildMeetingsSearchSupergraphCalendarSliceInput(filters, {
        past: true,
        first: 30,
        after: null,
        order: 'DESC',
      })
      : null),
    [filters, usePageList],
  )

  const visibleRangeCmdInput = useMemo(
    () => (visibleRangeQueryVariables && !useVisibleRangePageList
      ? buildSupergraphInput(visibleRangeQueryVariables, {
        first: VISIBLE_RANGE_PAGE_SIZE,
        after: null,
        order: 'ASC',
      })
      : null),
    [visibleRangeQueryVariables, useVisibleRangePageList],
  )

  const visibleRangeSearchInput = useMemo(
    () => (visibleRangeQueryVariables && useVisibleRangePageList
      ? buildMeetingsSearchSupergraphCalendarSliceInput(visibleRangeQueryVariables, {
        first: VISIBLE_RANGE_PAGE_SIZE,
        after: null,
        order: 'ASC',
      })
      : null),
    [visibleRangeQueryVariables, useVisibleRangePageList],
  )

  const skipBase = skipQueries || !isSupergraphEnabled

  const futureCmd = useListMeetingsSupergraphQuery(futureCmdInput ?? {}, {
    skip: skipBase || futureCmdInput == null,
    ...queryOptions,
  })

  const futureSearch = useMeetingsPageListSupergraphQuery(futureSearchInput ?? {}, {
    skip: skipBase || futureSearchInput == null,
    ...queryOptions,
  })

  const todayCmd = useListMeetingsSupergraphQuery(todayCmdInput ?? {}, {
    skip: skipBase || !shouldLoadToday || todayCmdInput == null,
    ...queryOptions,
  })

  const todaySearch = useMeetingsPageListSupergraphQuery(todaySearchInput ?? {}, {
    skip: skipBase || !shouldLoadToday || todaySearchInput == null,
    ...queryOptions,
  })

  const pastCmd = useListMeetingsSupergraphQuery(pastCmdInput ?? {}, {
    skip: skipBase || pastCmdInput == null || skipPastQuery,
    ...queryOptions,
  })

  const pastSearch = useMeetingsPageListSupergraphQuery(pastSearchInput ?? {}, {
    skip: skipBase || pastSearchInput == null || skipPastQuery,
    ...queryOptions,
  })

  const { data: visibleRangeDataCmd } = useListMeetingsSupergraphQuery(
    visibleRangeCmdInput ?? {},
    {
      ...backgroundPollingConfig,
      pollingInterval: visibleRangePollingInterval,
      skip: skipBase || !visibleRangeQueryVariables || visibleRangeCmdInput == null,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { data: visibleRangeDataSearch } = useMeetingsPageListSupergraphQuery(
    visibleRangeSearchInput ?? {},
    {
      ...backgroundPollingConfig,
      pollingInterval: visibleRangePollingInterval,
      skip: skipBase || !visibleRangeQueryVariables || visibleRangeSearchInput == null,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  )

  const visibleRangeDataSuper = useVisibleRangePageList ? visibleRangeDataSearch : visibleRangeDataCmd

  const [triggerCmd, resultCmd] = useLazyListMeetingsSupergraphQuery()
  const [triggerSearch, resultSearch] = useLazyMeetingsPageListSupergraphQuery()
  const [triggerVisibleRangeNextCmd, visibleRangeNextResultCmd] = useLazyListMeetingsSupergraphQuery()
  const [triggerVisibleRangeNextSearch, visibleRangeNextResultSearch] = useLazyMeetingsPageListSupergraphQuery()
  const [triggerMeetingSupergraph] = useLazyGetMeetingSupergraphQuery()

  const triggerSupergraph = useCallback((args) => {
    if (usePageList) {
      return triggerSearch(args)
    }
    return triggerCmd(args)
  }, [usePageList, triggerSearch, triggerCmd])

  const triggerVisibleRangeNextSupergraph = useCallback((args) => {
    if (useVisibleRangePageList) {
      return triggerVisibleRangeNextSearch(args)
    }
    return triggerVisibleRangeNextCmd(args)
  }, [useVisibleRangePageList, triggerVisibleRangeNextSearch, triggerVisibleRangeNextCmd])

  const resultSupergraph = usePageList ? resultSearch : resultCmd
  const visibleRangeNextResultSupergraph = useVisibleRangePageList
    ? visibleRangeNextResultSearch
    : visibleRangeNextResultCmd

  return {
    futureSupergraph: usePageList ? futureSearch : futureCmd,
    todaySupergraph: usePageList ? todaySearch : todayCmd,
    pastSupergraph: usePageList ? pastSearch : pastCmd,
    visibleRangeDataSuper,
    triggerSupergraph,
    resultSupergraph,
    triggerVisibleRangeNextSupergraph,
    visibleRangeNextResultSupergraph,
    triggerMeetingSupergraph,
  }
}
